import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang, usePath } from "../i18n";

const HERO_BG =
  "https://images.unsplash.com/photo-1765710686877-f7c433d88936?crop=entropy&cs=srgb&fm=jpg&q=72&w=1600";

const EASE = [0.76, 0, 0.24, 1];

const HERO_CERTS = [
  { img: "/cert-halal.png", label: "Halal Indonesia" },
  { img: "/cert-iso.png", label: "ISO 9001:2015" },
  { img: "/cert-iso22000.png", label: "ISO 22000" },
  { img: "/cert-sni.png", label: "SNI Indonesia" },
  { img: "/cert-bpom.png", label: "BPOM" },
];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: EASE, delay: 0.4 + i * 0.15 },
  }),
};

export const Hero = () => {
  const { t } = useLang();
  const lp = usePath();
  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden bg-clara-espresso noise-overlay">
      {/* Parallax background */}
      <motion.div
        initial={{ scale: 1.15 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.4, ease: EASE }}
        className="absolute inset-0"
      >
        <img src={HERO_BG} alt="Cocoa powder macro texture" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-clara-espresso via-clara-espresso/50 to-clara-espresso/70" />
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 min-h-screen flex flex-col justify-end pb-10 pt-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="eyebrow text-white mb-8"
          data-testid="hero-eyebrow"
        >
          PT Tan Putra Tama · Bekasi, Indonesia
        </motion.p>

        <h1 className="font-display font-light text-clara-cream leading-[0.92] tracking-tighter text-[16vw] sm:text-[13vw] lg:text-[10.5vw]">
          <span className="reveal-mask"><motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">{t.hero.line1}{" "}</motion.span></span>
          <span className="reveal-mask"><motion.span custom={1} variants={line} initial="hidden" animate="show" className="block italic text-clara-rust">{t.hero.line2}{" "}</motion.span></span>
          <span className="reveal-mask"><motion.span custom={2} variants={line} initial="hidden" animate="show" className="block">{t.hero.line3}</motion.span></span>
        </h1>

        <div className="mt-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.9 }}
            className="max-w-md text-clara-cream/75 text-base leading-relaxed"
            data-testid="hero-subheadline"
          >
            {t.hero.subheadline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7, duration: 0.9 }}
            className="flex items-center gap-4"
          >
            <Link to={lp("/contact")} data-testid="hero-request-sample-btn" className="inline-flex items-center rounded-full bg-clara-terracotta px-8 py-4 text-sm font-semibold text-clara-cream hover:bg-clara-cream hover:text-clara-espresso transition-colors duration-300">
              {t.hero.requestSample}
            </Link>
            <Link to={lp("/grades")} data-testid="hero-view-range-btn" className="inline-flex items-center rounded-full border border-clara-cream/40 px-8 py-4 text-sm font-semibold text-clara-cream hover:border-clara-cream transition-colors duration-300">
              {t.hero.viewRange}
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9, duration: 0.9 }}
          className="mt-10 border-t border-clara-cream/15 pt-6"
          data-testid="hero-cert-strip"
        >
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-6 sm:gap-x-10 gap-y-6">
            {HERO_CERTS.map((c) => (
              <div key={c.label} className="flex items-center gap-3" data-testid={`hero-cert-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="bg-clara-cream rounded-full h-10 w-10 sm:h-12 sm:w-12 flex items-center justify-center p-2 shrink-0">
                  <img src={c.img} alt={c.label} loading="lazy" decoding="async" className="max-h-full max-w-full object-contain" />
                </div>
                <span className="hidden sm:inline font-data text-[11px] tracking-[0.18em] uppercase text-clara-cream/70">{c.label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
